import React from 'react';
import AvatarIcon from 'components/features/utils/AvatarIcon';
import Loading from 'components/commons/loading/Loading';
import { useQuery } from 'react-query';
import { getPortfolioAnalysis } from 'config/api';
import {
  ManagementItem,
  ManagementType,
  PortfolioAnalysis as PortfolioAnalysisProps,
  PortfolioAnalysisItem,
  PortfolioAssetType,
} from 'components/features/target-achievement-support/utils/type';
import {
  getManagementItemInfo,
  portfolioTypeToBackgroundColor,
  portfolioTypeToString,
} from 'components/features/target-achievement-support/utils/portfolioInfo';
import { commaFormat } from 'components/features/new-report/utils/formatter';
import { PortfolioChart } from '../../portfolio-analysis/portfolio-chart';

const Chart = ({
  portfolios,
}: {
  portfolios: PortfolioAnalysisItem[];
}): React.ReactElement => {
  const options: Highcharts.Options = {
    legend: {
      enabled: false,
    },
    chart: {
      width: undefined,
      height: undefined,
      margin: 0,
    },
    title: {
      align: 'center',
      verticalAlign: 'middle',
      floating: true,
      y: 20,
    },
    plotOptions: {
      pie: {
        showInLegend: true,
      },
    },
    exporting: {
      enabled: false,
    },
  };
  return (
    <>
      <div className="grid gap-2" style={{ gridTemplateColumns: '1fr auto' }}>
        <PortfolioChart
          chart={{ title: '現在' }}
          data={portfolios.map(p => {
            return {
              id: p.assetType,
              value: p.currentPer,
            };
          })}
          idToColor={portfolioTypeToBackgroundColor}
          idToTitle={portfolioTypeToString}
          valueFormatter={value => {
            return `${commaFormat(value, { numberOfDecimals: 1 })}%`;
          }}
          options={options}
        />
        <div className="flex-col flex justify-center">
          <div
            className="flex flex-col p-4 gap-2"
            style={{ minHeight: '155px' }}>
            {portfolios.map((p, key) => {
              return (
                <div className="flex flex-row items-center" key={key}>
                  <div
                    className="rounded-full w-3 h-3"
                    style={{
                      backgroundColor: portfolioTypeToBackgroundColor(
                        p.assetType
                      ),
                    }}></div>
                  <div className="mx-2 text-xs whitespace-no-wrap">
                    {portfolioTypeToString(p.assetType)}
                  </div>
                  <div className="ml-auto text-xs">
                    {commaFormat(p.currentPer, {
                      numberOfDecimals: 1,
                    })}
                    %
                  </div>
                </div>
              );
            })}
          </div>
        </div>
      </div>
    </>
  );
};

const AccountTag = ({
  title,
  className,
}: {
  title: string;
  className?: string;
}): React.ReactElement => {
  return (
    <>
      <div
        className={`border border-bluegray-300 rounded text-xs bg-bluegray-150 px-1 ${className ??
          ''}`}
        style={{ color: '#3f3f46' }}>
        {title}
      </div>
    </>
  );
};

const PortfolioLoading = () => {
  return (
    <div
      className="flex justify-center rounded-lg border-bluegray-300 border p-4 bg-white mx-4 mt-2"
      style={{ height: '300px' }}>
      <Loading loading circularLoading />
    </div>
  );
};

type Props = {
  id: string;
  goPortfolioAnalysis: () => void;
  registeredAccounts: string[];
  isShowGoToPortfolio: boolean;
  className?: string;
};

export const PortfolioAnalysis = ({
  id,
  goPortfolioAnalysis,
  registeredAccounts,
  isShowGoToPortfolio,
  className,
}: Props): React.ReactElement => {
  const { data: portfolioAnalysis, isFetching } = useQuery(
    'portfolioAnalysisPortfolioAnalysis',
    () => getPortfolioAnalysisAPI(id),
    {
      refetchOnWindowFocus: false,
    }
  );

  if (
    portfolioAnalysis === null ||
    portfolioAnalysis === undefined ||
    isFetching
  )
    return <PortfolioLoading />;

  const managementType = portfolioAnalysis.managementType;
  const managementItemInfo = getManagementItemInfo(managementType);
  if (managementItemInfo === undefined || managementType === undefined)
    return <></>;

  const managementItems = portfolioAnalysis.managementItems as ManagementItem[];
  const items = managementItems.find(
    managementItem => managementItem.managementType === managementType
  );

  if (items === undefined) return <></>;

  const portfolios = managementItemInfo.dispType.map(dispType => {
    const itemsByPortfolioAnalysis = items.itemsByPortfolioAnalysis as PortfolioAnalysisItem[];
    return itemsByPortfolioAnalysis.find(item => item.assetType === dispType);
  }) as PortfolioAnalysisItem[] | undefined;

  if (portfolios === undefined) return <></>;

  return (
    <>
      <div
        className={`rounded-lg border-bluegray-300 border p-4 bg-white ${className ??
          ''}`}>
        <div className="flex flex-row items-start">
          <AvatarIcon icon="average" />
          <div className="ml-4 text-sm font-bold text-bluegray-700 flex flex-row flex-wrap">
            この目標に登録している口座の
            <div className="text-sm">ポートフォリオ</div> （資産配分）
            <div className="text-xs font-normal">※</div>
          </div>
        </div>
        <div className="mt-4 text-bluegray-700 text-sm">
          あなたの資産がどのアセット（投資対象）に多く配分されているか確認してみましょう。
          <br />
          リスクを抑えた運用をするには、各アセットに分散して投資する方法があります。
        </div>
        <div className="mt-10">
          <Chart {...{ portfolios }} />
        </div>
        <div
          className="grid items-center mt-6"
          style={{ gridTemplateColumns: 'auto 1fr' }}>
          <div className="text-sm text-bluegray-700">登録口座：</div>
          {registeredAccounts.length !== 0 ? (
            <div className="flex flex-wrap">
              {registeredAccounts.map((account, key) => (
                <div key={key}>
                  {account !== null && (
                    <>
                      <AccountTag
                        {...{ title: account }}
                        className="ml-1 mt-1"
                      />
                    </>
                  )}
                </div>
              ))}
            </div>
          ) : (
            <>
              <div className="text-sm text-bluegray-700">なし</div>
            </>
          )}
        </div>
        <div className="mt-8 text-xs text-bluegray-700">
          ※投資信託は、そのファンドが投資対象とするアセットに振り分けられています。また、アクティブファンドなど一部の投資信託はその他に分類されます。
        </div>
        {isShowGoToPortfolio && (
          <div
            className="rounded-lg border border-blue-700 bg-gray-50 text-blue-700 mt-4 font-bold p-2 cursor-pointer"
            onClick={goPortfolioAnalysis}>
            <div className="text-sm flex flex-row justify-center items-center">
              ＼最小リスクで運用／
            </div>
            <div className="text-base flex flex-row justify-center items-center">
              ポートフォリオの提案を見てみる
            </div>
          </div>
        )}
      </div>
    </>
  );
};

const getPortfolioAnalysisAPI = async (id: string) => {
  const ret = await getPortfolioAnalysis(id);
  return {
    itemsByPortfolioAnalysis: ret.ItemsByPortfolioAnalysis.map(item => {
      return {
        assetType: item.AssetType as PortfolioAssetType,
        currentPer: item.CurrentPer,
        targetPer: item.TargetPer,
      };
    }),
    registeredAccountName: ret.RegisteredAccountName,
    targetReturnPer: ret.TargetReturnPer,
    targetReturnLimitPer: ret.TargetReturnLimitPer,
    updateDateOfAsset: ret.UpdateDateOfAsset,
    managementType: ret.ManagementType as ManagementType,
    managementItems: ret.ManagementItems.map(managementItem => {
      return {
        managementType: managementItem.ManagementType as ManagementType,
        itemsByPortfolioAnalysis: managementItem.ItemsByPortfolioAnalysis.map(
          item => {
            return {
              assetType: item.AssetType as PortfolioAssetType,
              currentPer: item.CurrentPer,
              targetPer: item.TargetPer,
            };
          }
        ),
      };
    }),
  } as PortfolioAnalysisProps;
};
